/* Left column of the split hero: a typewriter-style manuscript.
 *
 * iter-72m — the animated "typed sentence + blinking cursor" was
 * lifted out into `NotepadTypewriter.jsx` which floats in the
 * whitespace between the headline column and the top-right bookmark.
 * This pane now renders only the eyebrow tag, the headline, the
 * static subline paragraph, and the safety note.
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ShieldCheck, BookOpen } from 'lucide-react';
import { P, FONTS, GRAIN_URL } from './palette';

export default function TypewriterPane({ headline, subline, safetyNote, interactive = true }) {
  const { t } = useTranslation();
  return (
    <div
      className="relative flex h-full flex-col justify-center px-6 py-12 lg:pl-16 lg:pr-8"
      style={{ backgroundColor: P.paper, color: P.ink }}
      data-testid="scriptorium-typewriter-pane"
    >
      {/* Ruler-style margin marks on either side */}
      <div
        className="pointer-events-none absolute inset-y-8 left-0 w-px opacity-30 hidden lg:block"
        style={{ background: `repeating-linear-gradient(to bottom, ${P.gold}, ${P.gold} 6px, transparent 6px, transparent 14px)` }}
      />
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.15]"
        style={{ backgroundImage: GRAIN_URL }}
      />

      <div className="relative">
        <div className="mb-4 flex flex-wrap items-center gap-x-3 gap-y-1.5">
          <span
            className="text-[10.5px] uppercase tracking-[0.32em]"
            style={{ color: P.oxblood, fontFamily: FONTS.type }}
          >
            {t('landing.deskDraftInProgress')}
          </span>
          {/* Feb-2026 · Relocated here from the top sign-in card, right
           * next to "The Desk" label it's describing an alternative to. */}
          {interactive ? (
            <Link
              to="/how-we-help-writers"
              data-testid="cb-nav-how-we-help"
              className="inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-[10.5px] font-medium uppercase tracking-[0.1em] transition hover:brightness-105"
              style={{ borderColor: `${P.oxblood}55`, color: P.oxblood, fontFamily: FONTS.type }}
            >
              <BookOpen className="h-3 w-3" /> {t('landing.howWeHelp')}
            </Link>
          ) : (
            <span
              className="inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-[10.5px] font-medium uppercase tracking-[0.1em]"
              style={{ borderColor: `${P.oxblood}55`, color: P.oxblood, fontFamily: FONTS.type }}
            >
              <BookOpen className="h-3 w-3" /> {t('landing.howWeHelp')}
            </span>
          )}
        </div>
        <h1
          className="max-w-xl text-3xl leading-[1.2]"
          style={{ fontFamily: FONTS.display, fontWeight: 700, color: P.ink }}
          data-testid="cb-hero-headline"
        >
          {headline}
        </h1>
        <div
          className="mt-6 max-w-md text-[16px] leading-[1.65]"
          style={{ color: P.inkSoft, fontFamily: FONTS.body }}
        >
          <p>{subline}</p>
        </div>

        <p className="mt-8 inline-flex items-center gap-2 text-[11px]" style={{ color: P.inkSoft }}>
          <ShieldCheck className="h-3.5 w-3.5" style={{ color: P.verdant }} />
          {safetyNote}
        </p>
      </div>
    </div>
  );
}
